import { motion } from "framer-motion";

const steps = [
  {
    n: "01",
    tag: "Subir",
    title: "Sube la prenda",
    body: "Una foto en fondo neutro es suficiente. Detectamos automáticamente el tejido, el peso y la caída de la prenda.",
    meta: ["JPG / PNG", "≤ 12MB", "auto-crop"],
  },
  {
    n: "02",
    tag: "Crear",
    title: "La IA arma la escena",
    body: "Elegimos modelo, luz, cámara, pose y escenario que encajan con tu prenda. Sin briefs, sin producción.",
    meta: ["3 motores a elegir", "3:4 · 1:1 · 9:16", "resultados coherentes"],
  },
  {
    n: "03",
    tag: "Descargar",
    title: "Descarga la campaña",
    body: "Recibes los recortes listos para tu tienda online, Instagram, TikTok o lookbook. Todo desde una sola foto.",
    meta: ["Hasta 4K", "3 formatos", "listo para publicar"],
  },
];

export function Pipeline() {
  return (
    <section id="pipeline" className="relative px-5 md:px-8 py-24 md:py-32">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between mb-14 md:mb-20 gap-6 flex-wrap">
          <div>
            <span className="tag mb-4 inline-flex" data-testid="pipeline-tag">§ 002 · Cómo funciona</span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-[8vw] md:text-[4.6vw] leading-[0.95] tracking-[-0.02em] text-[rgb(var(--paper))] max-w-3xl font-light"
            >
              El proceso, <span className="italic font-serif text-[rgb(var(--brand))]">en tres pasos.</span>
            </motion.h2>
          </div>
          <p className="text-[rgb(var(--paper-dim))] max-w-sm text-sm">
            De la foto de producto al set final. Simple, transparente y sin sorpresas.
          </p>
        </div>

        {/* Flow line + nodes */}
        <div className="relative">
          {/* connector line (desktop) — gradient rail with a traveling light pulse */}
          <div className="hidden md:block absolute top-[46px] left-[10%] right-[10%] h-[2px] overflow-visible">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-[rgb(var(--brand))]/35 to-transparent" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-[rgb(var(--brand-2))]/25 to-transparent" style={{ transform: "translateX(18%)" }} />
            <div className="pipeline-pulse absolute top-1/2 -translate-y-1/2 h-[3px] w-28 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((s, i) => (
              <motion.article
                key={s.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className="relative group"
                data-testid={`chapter-${s.n}`}
              >
                {/* node — hex-shaped tech badge */}
                <div className="relative flex justify-center mb-8">
                  <motion.div
                    whileHover={{ scale: 1.06, rotate: 3 }}
                    transition={{ type: "spring", stiffness: 220, damping: 15 }}
                    className="relative"
                  >
                    {/* soft outer glow ring */}
                    <span className="absolute -inset-3 rounded-full bg-[rgb(var(--brand))]/8 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    {/* hex badge */}
                    <div className="relative h-[92px] w-[92px] grid place-items-center">
                      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true">
                        <defs>
                          <linearGradient id={`hexgrad-${s.n}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="rgb(92,225,230)" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="rgb(124,92,255)" stopOpacity="0.15" />
                          </linearGradient>
                        </defs>
                        <polygon
                          points="50,4 92,27 92,73 50,96 8,73 8,27"
                          fill="rgba(10,14,22,0.9)"
                          stroke={`url(#hexgrad-${s.n})`}
                          strokeWidth="1.3"
                        />
                        <polygon
                          points="50,14 82,32 82,68 50,86 18,68 18,32"
                          fill="none"
                          stroke="rgb(92,225,230)"
                          strokeOpacity="0.2"
                          strokeWidth="0.8"
                        />
                      </svg>
                      <div className="relative flex flex-col items-center leading-none">
                        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[rgb(var(--paper-dim))] mb-1">
                          paso
                        </span>
                        <span className="font-display font-medium text-3xl text-[rgb(var(--brand))] text-glow">
                          {s.n}
                        </span>
                      </div>
                      {/* corner ticks */}
                      <span className="absolute top-0 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[rgb(var(--brand))]" />
                      <span className="absolute -bottom-0.5 right-2 h-1.5 w-1.5 rounded-full bg-[rgb(var(--brand))] glow-cyan" />
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  whileHover={{ scale: 1.025 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="card-tech p-6 md:p-7 origin-center"
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="tag">{s.tag}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))]">
                      paso · {s.n}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight text-[rgb(var(--paper))] mb-3 font-medium">
                    {s.title}
                  </h3>
                  <p className="text-[rgb(var(--paper-dim))] leading-relaxed text-sm md:text-[15px]">{s.body}</p>
                  <div className="mt-5 pt-5 border-t border-white/8 flex flex-wrap gap-2">
                    {s.meta.map((m) => (
                      <span key={m} className="tag">{m}</span>
                    ))}
                  </div>
                </motion.div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
